import { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Calendar as CalendarIcon, TrendingUp, Users } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  startOfYear,
} from 'date-fns';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { dashboardAPI, employeeAPI } from '@/lib/api';

// Metabase-style quick presets shown beside the calendar
const PRESETS = [
  {
    label: 'Current month',
    range: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }),
  },
  {
    label: 'Last 7 days',
    range: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Last 30 days',
    range: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Last month',
    range: () => {
      const prev = subMonths(new Date(), 1);
      return { from: startOfMonth(prev), to: endOfMonth(prev) };
    },
  },
  {
    label: 'Year to date',
    range: () => ({ from: startOfYear(new Date()), to: endOfDay(new Date()) }),
  },
];

const Analytics = () => {
  // Default: current month
  const [range, setRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [employeeId, setEmployeeId] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [totalScans, setTotalScans] = useState(0);
  const [loading, setLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    employeeAPI
      .list()
      .then((res) => setEmployees(res.data || []))
      .catch(() => setEmployees([]));
  }, []);

  const fetchAnalytics = useCallback(async () => {
    if (!range?.from || !range?.to) return;
    setLoading(true);
    try {
      const res = await dashboardAPI.getAnalytics({
        from: format(range.from, 'yyyy-MM-dd'),
        to: format(range.to, 'yyyy-MM-dd'),
        employeeId,
      });
      setData(res.data.series || []);
      setTotalScans(res.data.total_scans || 0);
    } catch (err) {
      setData([]);
      setTotalScans(0);
    } finally {
      setLoading(false);
    }
  }, [range, employeeId]);

  // Refetch whenever the date range or employee filter changes
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const applyPreset = (preset) => {
    setRange(preset.range());
    setCalendarOpen(false);
  };

  const rangeLabel =
    range?.from && range?.to
      ? `${format(range.from, 'dd MMM yyyy')} – ${format(range.to, 'dd MMM yyyy')}`
      : 'Select date range';

  // Cap the number of visible x-axis labels regardless of range length,
  // so long ranges (e.g. year-to-date) don't overlap into an unreadable smear.
  const MAX_TICKS = 10;
  const tickInterval = Math.max(0, Math.ceil(data.length / MAX_TICKS) - 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Analytics</h1>
        <p className="text-slate-600 mt-1">Card scan activity over time</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        {/* Date range picker */}
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="justify-start text-left font-normal min-w-[260px]"
              data-testid="date-range-btn"
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
              {rangeLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex flex-col sm:flex-row">
              {/* Presets */}
              <div className="border-b sm:border-b-0 sm:border-r border-slate-100 p-2 space-y-1 min-w-[150px]">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(preset)}
                    className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-700 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              {/* Custom range calendar */}
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                defaultMonth={range?.from}
                numberOfMonths={2}
                initialFocus
              />
            </div>
          </PopoverContent>
        </Popover>

        {/* Employee filter */}
        <Select value={employeeId} onValueChange={setEmployeeId}>
          <SelectTrigger className="min-w-[220px]" data-testid="employee-filter">
            <Users className="mr-2 h-4 w-4 text-slate-500" />
            <SelectValue placeholder="All Employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employees</SelectItem>
            {employees.map((emp) => (
              <SelectItem key={emp.id} value={emp.id}>
                {emp.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Total for the selected range */}
        <div className="sm:ml-auto flex items-center gap-2 text-sm">
          <TrendingUp className="h-4 w-4 text-[#009bff]" />
          <span className="text-slate-500">Total scans:</span>
          <span className="font-bold text-slate-900 text-base" data-testid="total-scans">
            {totalScans}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        {loading ? (
          <div className="h-[400px] flex items-center justify-center text-slate-400">
            Loading chart…
          </div>
        ) : data.length === 0 ? (
          <div className="h-[400px] flex items-center justify-center text-slate-500">
            No scan data for the selected period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eaed" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => format(new Date(d), 'dd MMM')}
                interval={tickInterval}
                minTickGap={40}
                tick={{ fontSize: 13, fill: '#5f6368' }}
                axisLine={{ stroke: '#e8eaed' }}
                tickLine={false}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                allowDecimals={false}
                orientation="right"
                tick={{ fontSize: 13, fill: '#5f6368' }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip
                labelFormatter={(d) => format(new Date(d), 'dd MMM yyyy')}
                formatter={(value) => [value, 'Scans']}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e8eaed',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  fontSize: '13px',
                }}
              />
              <Line
                type="linear"
                dataKey="scans"
                stroke="#1a73e8"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: '#1a73e8', stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default Analytics;
